import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, Crown, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Could not verify payment status');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;
      setPaymentData(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        toast.error('Payment expired');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-12">
          <Crown className="w-8 h-8 text-[#D4AF37]" />
          <span className="text-2xl font-medium">
            Ella <span className="gold-text">Gold</span>
          </span>
        </div>

        {/* Status Card */}
        <div className="glass rounded-2xl p-8">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
              </div>
              <h1 className="text-2xl font-medium mb-3">Verifying Payment</h1>
              <p className="text-gray-400 mb-6">
                Please wait while we verify your payment...
              </p>
              <div className="flex justify-center gap-1">
                {[0, 1, 2].map((i) => (
                  <div 
                    key={i}
                    className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center animate-fade-in">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="text-2xl font-medium mb-3">Payment Successful!</h1>
              <p className="text-gray-400 mb-6">
                Thank you for your purchase. Your content is now available in your dashboard.
              </p>
              {paymentData && (
                <div className="p-4 bg-white/5 rounded-lg mb-6">
                  <p className="text-sm text-gray-400">Amount paid</p>
                  <p className="text-3xl font-bold gold-text">
                    €{(paymentData.amount_total / 100).toFixed(2)}
                  </p>
                </div>
              )}
              <Button 
                onClick={() => navigate('/dashboard')}
                className="w-full btn-gold py-6 text-lg"
                data-testid="go-to-dashboard"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-500" />
              </div>
              <h1 className="text-2xl font-medium mb-3">Payment Failed</h1>
              <p className="text-gray-400 mb-6">
                Something went wrong with your payment. Please try again or contact support.
              </p>
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate('/packages')}
                  className="w-full btn-gold"
                  data-testid="try-again"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full border-white/10 text-white hover:bg-white/5"
                >
                  Back to Home
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Support Link */}
        <p className="mt-8 text-sm text-gray-500">
          Problems? Contact us at{' '}
          <a href="mailto:support@ellagold.com" className="text-[#D4AF37] hover:underline">
            support@ellagold.com
          </a>
        </p>
      </div>
    </div>
  );
}
